import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  Banknote, CreditCard, Globe, Minus, Package, PenLine, Plus, RefreshCw, Search, ShoppingBag, StickyNote, Trash2, TriangleAlert,
  User, UtensilsCrossed, X,
} from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { Textarea } from '@/components/Textarea';
import { useCompanyCurrency, useLabels, useWorkspace } from '@/contexts/WorkspaceContext';
import { catalogService, CatalogItem } from '@/services/catalog.service';
import { contactsService, Contact } from '@/services/contacts.service';
import {
  Order, OrderLineInput, OrderPaymentMethod, OrderPricing, ordersService, OrderType, ORDER_TYPE_LABEL, previewTotals,
} from '@/services/orders.service';
import { Chip, formatMoney, Segmented } from '@/pages/catalog/shared';
import { TYPE_ICON } from './orderShared';

interface DraftLine {
  key: string;
  lineUid?: string;
  itemUid?: string;
  name: string;
  unitPrice: number;
  /** Catalog price is authoritative; open-price and custom lines are editable here. */
  priceEditable: boolean;
  quantity: number;
  notes: string;
  notesOpen: boolean;
}

const PAY: Array<{ value: OrderPaymentMethod; label: string; icon: typeof Banknote }> = [
  { value: 'cash', label: 'Cash', icon: Banknote },
  { value: 'card', label: 'Card', icon: CreditCard },
  { value: 'online', label: 'Online', icon: Globe },
];
let keySeq = 0;
const newKey = () => `l${++keySeq}`;

export default function OrderComposer() {
  const t = useLabels();
  const navigate = useNavigate();
  const { orderUid } = useParams<{ orderUid?: string }>();
  const editing = !!orderUid;
  const { info } = useWorkspace();
  const companyCurrency = useCompanyCurrency();
  const noun = t('orders', 'singular');
  const guestWord = t('contacts', 'singular');

  const [catalog, setCatalog] = useState<CatalogItem[]>([]);
  const [catLoading, setCatLoading] = useState(true);
  const [catError, setCatError] = useState(false);
  const [pricing, setPricing] = useState<OrderPricing | null>(null);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<string | null>(null);

  const [original, setOriginal] = useState<Order | null>(null);
  const [lines, setLines] = useState<DraftLine[]>([]);
  const [type, setType] = useState<OrderType>('dine_in');
  const [tableLabel, setTableLabel] = useState('');
  const [address, setAddress] = useState('');
  const [guest, setGuest] = useState<{ contactUid: string; name: string; phone?: string } | null>(null);
  const [guestQuery, setGuestQuery] = useState('');
  const [guestResults, setGuestResults] = useState<Contact[]>([]);
  const [guestOpen, setGuestOpen] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discount, setDiscount] = useState('');
  const [notes, setNotes] = useState('');
  const [payNow, setPayNow] = useState<OrderPaymentMethod | null>(null);
  const [custom, setCustom] = useState<{ name: string; price: string } | null>(null);
  const [placing, setPlacing] = useState(false);
  const [lastPlaced, setLastPlaced] = useState<Order | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const ticketRef = useRef<HTMLDivElement>(null);

  const loadCatalog = async () => {
    setCatLoading(true);
    setCatError(false);
    try {
      const [list, p] = await Promise.all([catalogService.list({ limit: 200 }), ordersService.pricing()]);
      setCatalog(list.items);
      setPricing(p);
    } catch {
      setCatError(true);
    } finally {
      setCatLoading(false);
    }
  };
  useEffect(() => { void loadCatalog(); }, []);

  useEffect(() => {
    if (!orderUid) return;
    ordersService.get(orderUid).then((o) => {
      if (o.status !== 'new' && o.status !== 'preparing') {
        toast.error(`#${o.number} can’t be edited any more`);
        navigate(`/orders/${o.orderUid}`, { replace: true });
        return;
      }
      setOriginal(o);
      setType(o.type);
      setTableLabel(o.tableLabel ?? '');
      setAddress(o.deliveryAddress ?? '');
      setGuest(o.contactUid ? { contactUid: o.contactUid, name: o.customerName ?? guestWord, phone: o.customerPhone ?? undefined } : null);
      setCustomerName(o.contactUid ? '' : o.customerName ?? '');
      setCustomerPhone(o.contactUid ? '' : o.customerPhone ?? '');
      setDiscount(o.discount ? String(o.discount) : '');
      setNotes(o.notes ?? '');
      setLines(o.items.map((l) => ({
        key: newKey(), lineUid: l.lineUid, itemUid: l.itemUid ?? undefined, name: l.name, unitPrice: l.unitPrice,
        priceEditable: false, quantity: l.quantity, notes: l.notes ?? '', notesOpen: !!l.notes,
      })));
    }).catch(() => navigate('/orders', { replace: true }));
  }, [orderUid, navigate, guestWord]);

  // Guest search (debounced).
  useEffect(() => {
    const q = guestQuery.trim();
    if (q.length < 2) { setGuestResults([]); return; }
    const h = setTimeout(() => {
      contactsService.list({ q, limit: 6 }).then((r) => setGuestResults(r.items)).catch(() => setGuestResults([]));
    }, 250);
    return () => clearTimeout(h);
  }, [guestQuery]);

  const categories = useMemo(() => {
    const seen = new Map<string, number>();
    for (const i of catalog) if (i.category) seen.set(i.category, (seen.get(i.category) ?? 0) + 1);
    return Array.from(seen.entries());
  }, [catalog]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return catalog.filter((i) =>
      (category === null || i.category === category)
      && (!q || i.name.toLowerCase().includes(q) || (i.sku ?? '').toLowerCase().includes(q) || i.tags.some((tg) => tg.toLowerCase().includes(q))));
  }, [catalog, category, search]);

  const qtyOf = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lines) if (l.itemUid) m.set(l.itemUid, (m.get(l.itemUid) ?? 0) + l.quantity);
    return m;
  }, [lines]);

  const addItem = (it: CatalogItem) => {
    if (!it.isAvailable) return;
    setLines((ls) => {
      const idx = ls.findIndex((l) => l.itemUid === it.itemUid && !l.notes);
      if (idx >= 0) return ls.map((l, i) => (i === idx ? { ...l, quantity: Math.min(999, l.quantity + 1) } : l));
      return [...ls, {
        key: newKey(), itemUid: it.itemUid, name: it.name, unitPrice: it.price ?? 0, priceEditable: it.price === null,
        quantity: 1, notes: '', notesOpen: false,
      }];
    });
  };

  const patchLine = (key: string, patch: Partial<DraftLine>) => setLines((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  const removeLine = (key: string) => setLines((ls) => ls.filter((l) => l.key !== key));

  const addCustom = () => {
    if (!custom) return;
    const name = custom.name.trim();
    const price = Number(custom.price);
    if (!name) { toast.error('Name the custom item'); return; }
    if (!Number.isFinite(price) || price < 0) { toast.error('Enter a price'); return; }
    setLines((ls) => [...ls, { key: newKey(), name, unitPrice: Math.round(price * 100) / 100, priceEditable: true, quantity: 1, notes: '', notesOpen: false }]);
    setCustom(null);
  };

  const currency = original?.currency ?? pricing?.currency ?? companyCurrency;
  const taxRatePct = original?.taxRatePct ?? pricing?.taxRatePct ?? 0;
  const vatInclusive = original?.vatInclusive ?? pricing?.vatInclusive ?? false;
  const discountNum = Number(discount) || 0;
  const totals = previewTotals(lines, discountNum, taxRatePct, vatInclusive);
  const itemCount = lines.reduce((s, l) => s + l.quantity, 0);

  const problems: string[] = [];
  if (!lines.length) problems.push('Add at least one item');
  if (type === 'delivery' && !address.trim()) problems.push('Add the delivery address');

  const reset = () => {
    setLines([]); setTableLabel(''); setAddress(''); setGuest(null); setGuestQuery(''); setCustomerName(''); setCustomerPhone('');
    setDiscount(''); setNotes(''); setPayNow(null); setCustom(null); setSearch(''); setCategory(null);
    searchRef.current?.focus();
  };

  const submit = async () => {
    if (problems.length) { toast.error(problems[0]); return; }
    const items: OrderLineInput[] = lines.map((l) => ({
      lineUid: l.lineUid,
      itemUid: l.itemUid,
      name: l.itemUid ? undefined : l.name,
      unitPrice: l.priceEditable || !l.itemUid ? l.unitPrice : undefined,
      quantity: l.quantity,
      notes: l.notes.trim() || undefined,
    }));
    const body = {
      type,
      tableLabel: type === 'dine_in' ? tableLabel.trim() : '',
      deliveryAddress: type === 'delivery' ? address.trim() : '',
      contactUid: guest?.contactUid ?? '',
      customerName: guest ? guest.name : customerName.trim(),
      customerPhone: guest ? guest.phone ?? '' : customerPhone.trim(),
      items,
      discount: discountNum > 0 ? Math.round(discountNum * 100) / 100 : 0,
      notes: notes.trim(),
    };
    setPlacing(true);
    try {
      if (editing && original) {
        const o = await ordersService.update(original.orderUid, body);
        toast.success(`#${o.number} updated`);
        navigate(`/orders/${o.orderUid}`);
      } else {
        const o = await ordersService.create({ ...body, paymentMethod: payNow ?? undefined });
        toast.success(`${noun} #${o.number} placed`);
        setLastPlaced(o);
        reset();
      }
    } catch (e: any) {
      if (!e?.response) toast.error('Could not save — check your connection');
    } finally {
      setPlacing(false);
    }
  };

  const FoodIcon = info?.family?.key === 'food' ? UtensilsCrossed : Package;

  return (
    <>
      <Topbar
        title={editing ? `Edit #${original?.number ?? ''}` : `New ${noun.toLowerCase()}`}
        breadcrumb={`${t('orders')} / ${editing ? 'Edit' : 'New'}`}
        description={editing ? 'Change items, guest or notes while the kitchen hasn’t finished.' : 'Tap items to add them. Totals include VAT the way your company charges it.'}
        right={lastPlaced && !editing ? (
          <Link to={`/orders/${lastPlaced.orderUid}`} className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-success-soft px-3 text-xs font-semibold text-success hover:brightness-110">
            #{lastPlaced.number} placed · View
          </Link>
        ) : undefined}
      />

      <div className="w-full px-4 py-6 pb-28 sm:px-7 sm:py-7 lg:pb-7">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_400px] xl:grid-cols-[minmax(0,1fr)_440px]">
          {/* ── menu ── */}
          <section className="min-w-0 rounded-2xl border border-border bg-surface shadow-soft">
            <div className="space-y-3 border-b border-border p-4">
              <Input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${t('catalog').toLowerCase()}…`} iconLeft={<Search size={15} />} autoFocus
                iconRight={search ? <button type="button" aria-label="Clear" onClick={() => setSearch('')} className="hover:text-text-primary"><X size={14} /></button> : undefined}
                onKeyDown={(e) => { if (e.key === 'Enter' && shown.length === 1) { addItem(shown[0]); setSearch(''); } }} />
              {categories.length > 0 && (
                <div className="flex gap-2 overflow-x-auto pb-0.5 [scrollbar-width:none]">
                  <Chip active={category === null} onClick={() => setCategory(null)}>All</Chip>
                  {categories.map(([c, n]) => <Chip key={c} active={category === c} onClick={() => setCategory(category === c ? null : c)} count={n}>{c}</Chip>)}
                </div>
              )}
            </div>
            <div className="p-4">
              {catError ? (
                <div className="flex flex-col items-center gap-3 py-12 text-center">
                  <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-danger-soft text-danger"><TriangleAlert size={20} /></span>
                  <p className="text-sm font-semibold text-text-primary">Couldn’t load your {t('catalog').toLowerCase()}</p>
                  <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={14} />} onClick={() => void loadCatalog()}>Retry</Button>
                </div>
              ) : catLoading ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                  {Array.from({ length: 8 }).map((_, i) => <div key={i} className="skeleton h-[132px] rounded-xl" />)}
                </div>
              ) : catalog.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-12 text-center">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-soft text-primary"><FoodIcon size={22} /></span>
                  <p className="text-sm font-semibold text-text-primary">Your {t('catalog').toLowerCase()} is empty</p>
                  <p className="max-w-xs text-xs text-text-muted">Add items once and they’re one tap away here. You can still add a custom item on the right.</p>
                  <Link to="/catalog" className="mt-1 text-xs font-semibold text-primary hover:underline">Open {t('catalog')} →</Link>
                </div>
              ) : shown.length === 0 ? (
                <p className="py-12 text-center text-sm text-text-muted">Nothing matches “{search}”.</p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                  {shown.map((it) => {
                    const q = qtyOf.get(it.itemUid) ?? 0;
                    return (
                      <button
                        key={it.itemUid}
                        type="button"
                        disabled={!it.isAvailable}
                        onClick={() => addItem(it)}
                        className={clsx(
                          'group relative flex flex-col overflow-hidden rounded-xl border text-left transition-all',
                          !it.isAvailable ? 'cursor-not-allowed border-border opacity-50'
                            : q > 0 ? 'border-primary shadow-glow' : 'border-border hover:-translate-y-0.5 hover:border-primary/50 hover:shadow-elevated active:translate-y-0',
                        )}
                      >
                        <div className="relative h-20 w-full bg-surface-alt">
                          {it.imageUrl ? <img src={it.imageUrl} alt="" loading="lazy" className="h-full w-full object-cover" /> : (
                            <span className="flex h-full w-full items-center justify-center bg-gradient-to-br from-primary/15 to-primary/5 text-primary"><FoodIcon size={20} strokeWidth={1.6} /></span>
                          )}
                          {q > 0 && <span className="absolute right-1.5 top-1.5 flex h-6 min-w-6 items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold text-on-primary shadow-soft">{q}</span>}
                          {!it.isAvailable && <span className="absolute inset-x-0 bottom-0 bg-black/60 py-0.5 text-center text-2xs font-semibold uppercase tracking-wider text-white">Unavailable</span>}
                        </div>
                        <div className="flex flex-1 flex-col justify-between gap-0.5 p-2.5">
                          <span className="line-clamp-2 text-[12.5px] font-semibold leading-snug text-text-primary">{it.name}</span>
                          <span className="text-xs font-bold tabular-nums text-primary">{it.price === null ? 'Set price' : formatMoney(it.price, it.currency ?? currency)}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* ── ticket ── */}
          <aside ref={ticketRef} className="min-w-0 lg:sticky lg:top-24 lg:self-start">
            <div className="flex flex-col rounded-2xl border border-border bg-surface shadow-soft lg:max-h-[calc(100vh-7rem)]">
              <div className="space-y-3 border-b border-border p-4">
                <Segmented
                  value={type}
                  onChange={setType}
                  className="w-full"
                  options={(['dine_in', 'takeaway', 'delivery'] as OrderType[]).map((ty) => {
                    const Icon = TYPE_ICON[ty];
                    return { value: ty, label: ORDER_TYPE_LABEL[ty], icon: <Icon size={14} /> };
                  })}
                />
                {type === 'dine_in' && (
                  <Input value={tableLabel} onChange={(e) => setTableLabel(e.target.value)} placeholder="Table (e.g. 4, Patio 2)" maxLength={40} aria-label="Table" />
                )}
                {type === 'delivery' && (
                  <Textarea value={address} onChange={(e) => setAddress(e.target.value)} rows={2} maxLength={500} placeholder="Delivery address *" aria-label="Delivery address" />
                )}

                {guest ? (
                  <div className="flex items-center justify-between gap-2 rounded-xl border border-primary/30 bg-primary-soft/50 px-3 py-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-on-primary">{guest.name.slice(0, 1).toUpperCase()}</span>
                      <div className="min-w-0">
                        <div className="truncate text-[13px] font-semibold text-text-primary">{guest.name}</div>
                        {guest.phone && <div className="truncate text-2xs text-text-muted">{guest.phone}</div>}
                      </div>
                    </div>
                    <button type="button" aria-label={`Remove ${guestWord.toLowerCase()}`} onClick={() => setGuest(null)} className="rounded-lg p-1 text-text-muted hover:bg-surface hover:text-text-primary"><X size={14} /></button>
                  </div>
                ) : (
                  <div className="relative">
                    <Input
                      value={guestQuery}
                      onChange={(e) => { setGuestQuery(e.target.value); setGuestOpen(true); }}
                      onFocus={() => setGuestOpen(true)}
                      onBlur={() => setTimeout(() => setGuestOpen(false), 150)}
                      placeholder={`Find a ${guestWord.toLowerCase()} (name or phone)`}
                      iconLeft={<User size={15} />}
                      aria-label={`Find a ${guestWord.toLowerCase()}`}
                    />
                    {guestOpen && guestResults.length > 0 && (
                      <ul className="absolute inset-x-0 top-full z-20 mt-1 overflow-hidden rounded-xl border border-border bg-surface shadow-elevated">
                        {guestResults.map((c) => (
                          <li key={c.contactUid}>
                            <button type="button" onMouseDown={(e) => e.preventDefault()}
                              onClick={() => { setGuest({ contactUid: c.contactUid, name: [c.firstName, c.lastName].filter(Boolean).join(' '), phone: c.phone }); setGuestQuery(''); setGuestOpen(false); }}
                              className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-surface-alt">
                              <span className="truncate text-[13px] font-medium text-text-primary">{[c.firstName, c.lastName].filter(Boolean).join(' ')}</span>
                              <span className="shrink-0 text-2xs tabular-nums text-text-muted">{c.phone}</span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Input value={customerName} onChange={(e) => setCustomerName(e.target.value)} placeholder="Or quick name" maxLength={120} aria-label="Customer name" className="h-9 text-[13px]" />
                      <Input value={customerPhone} onChange={(e) => setCustomerPhone(e.target.value)} placeholder="Phone" maxLength={40} inputMode="tel" aria-label="Customer phone" className="h-9 text-[13px]" />
                    </div>
                  </div>
                )}
              </div>

              <div className="min-h-[120px] flex-1 overflow-y-auto">
                {lines.length === 0 ? (
                  <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-surface-alt text-text-muted"><ShoppingBag size={20} /></span>
                    <p className="text-sm font-medium text-text-secondary">No items yet</p>
                    <p className="text-xs text-text-muted">Tap something on the left to add it.</p>
                  </div>
                ) : (
                  <ul className="divide-y divide-border">
                    {lines.map((l) => (
                      <li key={l.key} className="px-4 py-3">
                        <div className="flex items-start gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-[13.5px] font-semibold leading-snug text-text-primary">{l.name}</div>
                            {l.priceEditable ? (
                              <label className="mt-1 inline-flex items-center gap-1.5 text-2xs text-text-muted">
                                {currency}
                                <input
                                  value={Number.isFinite(l.unitPrice) ? String(l.unitPrice) : ''}
                                  onChange={(e) => patchLine(l.key, { unitPrice: Math.max(0, Number(e.target.value.replace(',', '.')) || 0) })}
                                  inputMode="decimal"
                                  className="h-7 w-20 rounded-md border border-border bg-surface px-2 text-xs tabular-nums text-text-primary focus:border-primary focus:outline-none"
                                  aria-label={`Price of ${l.name}`}
                                />
                                each
                              </label>
                            ) : (
                              <div className="text-2xs text-text-muted">{formatMoney(l.unitPrice, currency)} each</div>
                            )}
                          </div>
                          <div className="flex items-center rounded-lg border border-border">
                            <button type="button" aria-label="Decrease" onClick={() => (l.quantity <= 1 ? removeLine(l.key) : patchLine(l.key, { quantity: l.quantity - 1 }))}
                              className="flex h-8 w-8 items-center justify-center text-text-secondary hover:text-text-primary">
                              {l.quantity <= 1 ? <Trash2 size={13} /> : <Minus size={13} />}
                            </button>
                            <span className="w-7 text-center text-sm font-bold tabular-nums text-text-primary">{l.quantity}</span>
                            <button type="button" aria-label="Increase" onClick={() => patchLine(l.key, { quantity: Math.min(999, l.quantity + 1) })}
                              className="flex h-8 w-8 items-center justify-center text-text-secondary hover:text-text-primary">
                              <Plus size={13} />
                            </button>
                          </div>
                          <div className="w-[76px] shrink-0 text-right text-[13px] font-semibold tabular-nums text-text-primary">{formatMoney(Math.round(l.unitPrice * l.quantity * 100) / 100, currency)}</div>
                        </div>
                        {l.notesOpen ? (
                          <input
                            value={l.notes}
                            onChange={(e) => patchLine(l.key, { notes: e.target.value })}
                            maxLength={300}
                            autoFocus={!l.notes}
                            placeholder="e.g. no onions, extra spicy"
                            className="mt-2 h-8 w-full rounded-md border border-border bg-surface-alt px-2.5 text-xs text-text-primary placeholder:text-text-muted focus:border-primary focus:outline-none"
                            aria-label={`Notes for ${l.name}`}
                          />
                        ) : (
                          <button type="button" onClick={() => patchLine(l.key, { notesOpen: true })} className="mt-1 inline-flex items-center gap-1 text-2xs font-medium text-text-muted hover:text-primary">
                            <StickyNote size={11} /> Add note
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="px-4 pb-3">
                  {custom ? (
                    <div className="flex items-center gap-2 rounded-xl border border-dashed border-border p-2">
                      <Input value={custom.name} onChange={(e) => setCustom({ ...custom, name: e.target.value })} placeholder="Custom item" className="h-8 text-xs" autoFocus maxLength={120}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} />
                      <Input value={custom.price} onChange={(e) => setCustom({ ...custom, price: e.target.value.replace(',', '.') })} placeholder="0.00" inputMode="decimal" className="h-8 w-24 text-xs"
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }} />
                      <Button size="sm" type="button" onClick={addCustom}>Add</Button>
                      <button type="button" aria-label="Cancel custom item" onClick={() => setCustom(null)} className="p-1 text-text-muted hover:text-text-primary"><X size={14} /></button>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setCustom({ name: '', price: '' })} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                      <PenLine size={12} /> Custom item
                    </button>
                  )}
                </div>
              </div>

              <div className="space-y-3 border-t border-border bg-surface-alt/40 p-4">
                <div className="grid grid-cols-[1fr_120px] gap-2">
                  <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={`${noun} note`} maxLength={1000} className="h-9 text-[13px]" aria-label="Order note" />
                  <Input value={discount} onChange={(e) => setDiscount(e.target.value.replace(',', '.').replace(/[^\d.]/g, ''))} placeholder="Discount" inputMode="decimal" className="h-9 text-[13px]" aria-label="Discount" />
                </div>
                <dl className="space-y-1 text-[13px]">
                  <div className="flex justify-between"><dt className="text-text-muted">Subtotal · {itemCount} item{itemCount === 1 ? '' : 's'}</dt><dd className="tabular-nums text-text-primary">{formatMoney(totals.subtotal, currency)}</dd></div>
                  {totals.discount > 0 && <div className="flex justify-between"><dt className="text-text-muted">Discount</dt><dd className="tabular-nums text-text-primary">− {formatMoney(totals.discount, currency)}</dd></div>}
                  {taxRatePct > 0 && (
                    <div className="flex justify-between"><dt className="text-text-muted">VAT {taxRatePct}%{vatInclusive ? ' (included)' : ''}</dt><dd className="tabular-nums text-text-muted">{formatMoney(totals.taxAmount, currency)}</dd></div>
                  )}
                  <div className="flex items-baseline justify-between pt-1">
                    <dt className="font-semibold text-text-primary">Total</dt>
                    <dd className="font-display text-2xl font-bold tabular-nums text-text-primary">{formatMoney(totals.total, currency)}</dd>
                  </div>
                </dl>
                {!editing && (
                  <div>
                    <div className="mb-1.5 text-2xs font-semibold uppercase tracking-[0.1em] text-text-muted">Paid now? <span className="font-normal normal-case tracking-normal">(optional)</span></div>
                    <div className="grid grid-cols-3 gap-2">
                      {PAY.map((m) => (
                        <button key={m.value} type="button" aria-pressed={payNow === m.value} onClick={() => setPayNow(payNow === m.value ? null : m.value)}
                          className={clsx('inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border text-xs font-semibold transition-colors',
                            payNow === m.value ? 'border-success bg-success-soft text-success' : 'border-border text-text-secondary hover:border-border-strong hover:text-text-primary')}>
                          <m.icon size={14} /> {m.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <Button size="lg" className="w-full" loading={placing} disabled={problems.length > 0} onClick={() => void submit()}>
                  {editing ? 'Save changes' : `Place ${noun.toLowerCase()} · ${formatMoney(totals.total, currency)}`}
                </Button>
                {problems.length > 0 && lines.length > 0 && <p className="text-center text-2xs text-warning">{problems[0]}</p>}
                {editing && original && (
                  <Link to={`/orders/${original.orderUid}`} className="block text-center text-xs font-medium text-text-muted hover:text-text-primary">Discard changes</Link>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Phones: the ticket sits below the menu — keep the total and a jump to it in reach. */}
      {lines.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-surface/95 px-4 py-3 shadow-elevated backdrop-blur lg:hidden">
          <button type="button" onClick={() => ticketRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
            className="flex h-12 w-full items-center justify-between rounded-xl bg-gold-gradient px-4 text-on-primary shadow-soft">
            <span className="flex items-center gap-2 text-sm font-semibold"><span className="flex h-6 min-w-6 items-center justify-center rounded-full bg-white/25 px-1.5 text-xs">{itemCount}</span> Review {noun.toLowerCase()}</span>
            <span className="font-display text-base font-bold tabular-nums">{formatMoney(totals.total, currency)}</span>
          </button>
        </div>
      )}
    </>
  );
}
